/**
 * LossesFilters Component
 *
 * Filter bar for losses list.
 * @see Sprint 13 - Pertes
 * @see US-13.6 - UI Page Pertes
 */

import { X, Search } from 'lucide-react';
import { Input, Select } from '@/components/ui';
import { DatePicker, type DateRange } from '@/components/ui';
import { useBrandContext } from '@/hooks';
import {
  LossReason,
  LOSS_REASON_LABELS,
  LOSS_TYPE_OPTIONS,
} from '@bstock/shared';

interface LossesFiltersProps {
  dateRange: DateRange;
  lossReason: string;
  lossType: string;
  search: string;
  onDateRangeChange: (range: DateRange) => void;
  onLossReasonChange: (lossReason: string) => void;
  onLossTypeChange: (lossType: string) => void;
  onSearchChange: (search: string) => void;
}

const lossReasonFilterOptions = [
  { value: '', label: 'Tous les motifs' },
  { value: LossReason.DLC, label: LOSS_REASON_LABELS[LossReason.DLC] },
  { value: LossReason.CASSE, label: LOSS_REASON_LABELS[LossReason.CASSE] },
  { value: LossReason.VOL, label: LOSS_REASON_LABELS[LossReason.VOL] },
  { value: LossReason.PRODUCTION, label: LOSS_REASON_LABELS[LossReason.PRODUCTION] },
  { value: LossReason.AUTRE, label: LOSS_REASON_LABELS[LossReason.AUTRE] },
];

export function LossesFilters({
  dateRange,
  lossReason,
  lossType,
  search,
  onDateRangeChange,
  onLossReasonChange,
  onLossTypeChange,
  onSearchChange,
}: LossesFiltersProps) {
  const { isCentral } = useBrandContext();

  // US-41.7: CENTRAL cannot have FINISHED losses, hide the option
  const lossTypeFilterOptions = [
    { value: '', label: 'Tous les types' },
    ...LOSS_TYPE_OPTIONS.filter(opt => !(isCentral && opt.value === 'FINISHED')),
  ];

  const hasFilters = lossReason || lossType || search;

  const handleReset = () => {
    onLossReasonChange('');
    onLossTypeChange('');
    onSearchChange('');
  };

  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="w-72">
        <label className="block text-sm font-medium text-dark-500 mb-1.5">
          Periode
        </label>
        <DatePicker
          value={dateRange}
          onChange={onDateRangeChange}
          placeholder="Selectionner une periode"
          showTime
        />
      </div>

      <Select
        value={lossReason}
        onChange={onLossReasonChange}
        options={lossReasonFilterOptions}
        allowEmpty={false}
        className="w-48"
      />

      <Select
        value={lossType}
        onChange={onLossTypeChange}
        options={lossTypeFilterOptions}
        allowEmpty={false}
        className="w-48"
      />

      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-400" />
        <Input
          type="text"
          placeholder="Rechercher un produit..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-10"
        />
      </div>

      {hasFilters && (
        <button
          onClick={handleReset}
          className="text-sm text-neutral-500 hover:text-dark-500 flex items-center gap-1 transition-colors"
        >
          <X className="w-4 h-4" />
          Reinitialiser
        </button>
      )}
    </div>
  );
}
